// src/pages/finance-portal/tax-notices/TaxNotices.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    RECEIVED: { bg: '#fef3c7', color: '#92400e' },
    IN_REVIEW: { bg: '#dbeafe', color: '#1e40af' },
    RESPONSE_DRAFTED: { bg: '#ede9fe', color: '#6d28d9' },
    RESPONSE_SUBMITTED: { bg: '#e0e7ff', color: '#3730a3' },
    RESOLVED: { bg: '#dcfce7', color: '#166534' },
    ESCALATED: { bg: '#fecaca', color: '#991b1b' },
};

const NOTICE_TYPES = [
    { value: '', label: 'All Types' },
    { value: 'CP2000', label: 'CP2000 - Income Discrepancy' },
    { value: 'CP501', label: 'CP501 - Balance Due' },
    { value: 'CP504', label: 'CP504 - Intent to Levy' },
    { value: 'LTR12C', label: 'LTR12C - Info Request' },
    { value: 'AUDIT', label: 'Audit Notice' },
    { value: 'PENALTY', label: 'Penalty Notice' },
    { value: 'STATE', label: 'State Notice' },
    { value: 'OTHER', label: 'Other' },
];

const STATUSES = [
    { value: '', label: 'All Statuses' },
    { value: 'RECEIVED', label: 'Received' },
    { value: 'IN_REVIEW', label: 'In Review' },
    { value: 'RESPONSE_DRAFTED', label: 'Response Drafted' },
    { value: 'RESPONSE_SUBMITTED', label: 'Response Submitted' },
    { value: 'RESOLVED', label: 'Resolved' },
    { value: 'ESCALATED', label: 'Escalated' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function daysUntil(dateStr) {
    if (!dateStr) return Infinity;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function TaxNotices() {
    const [notices, setNotices] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Filters
    const [noticeType, setNoticeType] = useState('');
    const [taxYear, setTaxYear] = useState('');
    const [status, setStatus] = useState('');

    // Add/Edit Notice Dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        notice_type: 'CP2000', notice_number: '', issuing_authority: 'IRS',
        tax_year: new Date().getFullYear(), received_date: '', response_due_date: '',
        amount_assessed: '', description: '', assigned_to: '',
    });
    const [saving, setSaving] = useState(false);

    // Resolve Dialog
    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolveId, setResolveId] = useState(null);
    const [resolveData, setResolveData] = useState({ resolution_notes: '', amount_resolved: '' });
    const [resolving, setResolving] = useState(false);

    const fetchNotices = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (noticeType) params.notice_type = noticeType;
            if (taxYear) params.tax_year = taxYear;
            if (status) params.status = status;
            const res = await axiosInstance.get('/api/finance/tax/notices', { params });
            if (res.data.success) {
                setNotices(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load notices');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, noticeType, taxYear, status]);

    useEffect(() => { fetchNotices(); }, [fetchNotices]);

    // Urgent notices (response due within 14 days)
    const urgentNotices = notices.filter(n =>
        n.response_due_date && n.status !== 'RESOLVED' && n.status !== 'RESPONSE_SUBMITTED' && daysUntil(n.response_due_date) <= 14 && daysUntil(n.response_due_date) >= 0
    );

    const openAddForm = () => {
        setEditingId(null);
        setFormData({
            notice_type: 'CP2000', notice_number: '', issuing_authority: 'IRS',
            tax_year: new Date().getFullYear(), received_date: '', response_due_date: '',
            amount_assessed: '', description: '', assigned_to: '',
        });
        setFormOpen(true);
    };

    const openEditForm = (notice) => {
        setEditingId(notice.id);
        setFormData({
            notice_type: notice.notice_type || 'CP2000',
            notice_number: notice.notice_number || '',
            issuing_authority: notice.issuing_authority || 'IRS',
            tax_year: notice.tax_year || new Date().getFullYear(),
            received_date: notice.received_date ? notice.received_date.split('T')[0] : '',
            response_due_date: notice.response_due_date ? notice.response_due_date.split('T')[0] : '',
            amount_assessed: notice.amount_assessed || '',
            description: notice.description || '',
            assigned_to: notice.assigned_to || '',
        });
        setFormOpen(true);
    };

    const handleSaveNotice = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                tax_year: parseInt(formData.tax_year, 10),
                amount_assessed: parseFloat(formData.amount_assessed) || 0,
            };
            if (editingId) {
                await axiosInstance.put(`/api/finance/tax/notices/${editingId}`, payload);
                setSuccess('Notice updated');
            } else {
                await axiosInstance.post('/api/finance/tax/notices', payload);
                setSuccess('Notice added');
            }
            setFormOpen(false);
            fetchNotices();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save notice');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this notice?')) return;
        try {
            await axiosInstance.delete(`/api/finance/tax/notices/${id}`);
            setSuccess('Notice deleted');
            fetchNotices();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete notice');
        }
    };

    const openResolve = (notice) => {
        setResolveId(notice.id);
        setResolveData({ resolution_notes: '', amount_resolved: notice.amount_assessed || '' });
        setResolveOpen(true);
    };

    const handleResolve = async () => {
        setResolving(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/tax/notices/${resolveId}/resolve`, {
                resolution_notes: resolveData.resolution_notes,
                amount_resolved: parseFloat(resolveData.amount_resolved) || 0,
            });
            setSuccess('Notice resolved');
            setResolveOpen(false);
            fetchNotices();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resolve notice');
        } finally {
            setResolving(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <WarningAmberIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Notices</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Track IRS and state tax notices</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openAddForm}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Add Notice
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Urgent Alert Banner */}
                {urgentNotices.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', fontFamily, fontWeight: 600 }}>
                        {urgentNotices.length} notice{urgentNotices.length > 1 ? 's' : ''} with response due within 14 days!
                        {urgentNotices.map(n => (
                            <Typography key={n.id} sx={{ fontFamily, fontSize: '0.85rem', mt: 0.5 }}>
                                {n.notice_type} #{n.notice_number} \u2014 Due: {formatDate(n.response_due_date)} ({daysUntil(n.response_due_date)} days left)
                            </Typography>
                        ))}
                    </Alert>
                )}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" select label="Notice Type" value={noticeType}
                        onChange={e => { setNoticeType(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 200 }}
                    >
                        {NOTICE_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                    <TextField
                        size="small" select label="Status" value={status}
                        onChange={e => { setStatus(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 160 }}
                    >
                        {STATUSES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                    </TextField>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Type', 'Notice #', 'Authority', 'Tax Year', 'Received', 'Response Due', 'Amount', 'Status', 'Assigned To', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : notices.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No notices found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : notices.map(n => {
                                    const dueSoon = n.response_due_date && n.status !== 'RESOLVED' && daysUntil(n.response_due_date) <= 14 && daysUntil(n.response_due_date) >= 0;
                                    return (
                                        <TableRow key={n.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, bgcolor: dueSoon ? '#fffbeb' : 'inherit' }}>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={n.notice_type} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: '#f1f5f9', color: '#475569' }} />
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{n.notice_number || '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{n.issuing_authority || '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{n.tax_year}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(n.received_date)}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, color: dueSoon ? '#dc2626' : '#1e293b', fontWeight: dueSoon ? 700 : 400 }}>
                                                {formatDate(n.response_due_date)}
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(n.amount_assessed)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={getStatusLabel(n.status)} size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                        bgcolor: (STATUS_COLORS[n.status] || STATUS_COLORS.RECEIVED).bg,
                                                        color: (STATUS_COLORS[n.status] || STATUS_COLORS.RECEIVED).color,
                                                    }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{n.assigned_to || '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {n.status !== 'RESOLVED' && (
                                                        <Tooltip title="Resolve">
                                                            <IconButton size="small" onClick={() => openResolve(n)}>
                                                                <CheckCircleIcon sx={{ fontSize: 18, color: ACCENT }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => openEditForm(n)}>
                                                            <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDelete(n.id)}>
                                                            <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Add/Edit Notice Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{editingId ? 'Edit Notice' : 'Add Notice'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Notice Type" required
                                value={formData.notice_type}
                                onChange={e => setFormData(prev => ({ ...prev, notice_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {NOTICE_TYPES.filter(t => t.value).map(t => (
                                    <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Notice Number"
                                value={formData.notice_number}
                                onChange={e => setFormData(prev => ({ ...prev, notice_number: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Issuing Authority"
                                value={formData.issuing_authority}
                                onChange={e => setFormData(prev => ({ ...prev, issuing_authority: e.target.value }))}
                                placeholder="e.g. IRS, CA FTB"
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Tax Year" type="number" required
                                value={formData.tax_year}
                                onChange={e => setFormData(prev => ({ ...prev, tax_year: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Received Date" type="date"
                                value={formData.received_date}
                                onChange={e => setFormData(prev => ({ ...prev, received_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Response Due Date" type="date"
                                value={formData.response_due_date}
                                onChange={e => setFormData(prev => ({ ...prev, response_due_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Amount Assessed" type="number"
                                value={formData.amount_assessed}
                                onChange={e => setFormData(prev => ({ ...prev, amount_assessed: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Assigned To"
                                value={formData.assigned_to}
                                onChange={e => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Description" multiline rows={3}
                                value={formData.description}
                                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSaveNotice} variant="contained" disabled={saving || !formData.notice_type}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resolve Dialog */}
            <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Resolve Notice</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Resolution Notes" multiline rows={3} required
                                value={resolveData.resolution_notes}
                                onChange={e => setResolveData(prev => ({ ...prev, resolution_notes: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Amount Resolved" type="number"
                                value={resolveData.amount_resolved}
                                onChange={e => setResolveData(prev => ({ ...prev, amount_resolved: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setResolveOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleResolve} variant="contained" disabled={resolving || !resolveData.resolution_notes}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {resolving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Resolve'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
